import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function DoctorSupervision() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);

  const options = [
    {
      icon: 'ri-shield-user-line',
      badge: t('supervision.option1.badge'),
      title: t('supervision.option1.title'),
      headline: 'Someone in your corner who answers only to you.',
      features: [
        t('supervision.option1.feature1'),
        t('supervision.option1.feature2'),
        t('supervision.option1.feature3'),
        t('supervision.option1.feature4'),
        t('supervision.option1.feature5'),
      ],
    },
    {
      icon: 'ri-user-heart-line',
      badge: t('supervision.option2.badge'),
      title: t('supervision.option2.title'),
      headline: 'The dentist you already trust stays part of your journey.',
      features: [
        t('supervision.option2.feature1'),
        t('supervision.option2.feature2'),
        t('supervision.option2.feature3'),
        t('supervision.option2.feature4'),
        t('supervision.option2.feature5'),
      ],
    },
    {
      icon: 'ri-team-line',
      badge: t('supervision.option3.badge'),
      title: t('supervision.option3.title'),
      headline: 'A senior specialist with 15+ years watches over every step.',
      features: [
        t('supervision.option3.feature1'),
        t('supervision.option3.feature2'),
        t('supervision.option3.feature3'),
        t('supervision.option3.feature4'),
        t('supervision.option3.feature5'),
      ],
    },
  ];

  const active = options[activeIndex];

  return (
    <section id="supervision" className="py-24 md:py-36 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-14 md:mb-20">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-px bg-amber-400/50" />
            <span
              className="text-amber-400/80 text-xs font-medium tracking-[0.25em] uppercase"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Supervision
            </span>
          </div>
          <h2
            className="text-4xl md:text-6xl font-normal text-white leading-tight max-w-3xl"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
          >
            {t('supervision.title')}
          </h2>
        </div>

        {/* Tab switcher */}
        <div className="flex flex-wrap gap-0 mb-12 border border-white/10">
          {options.map((opt, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`px-6 py-3.5 text-xs font-medium tracking-widest uppercase transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeIndex === i
                  ? 'bg-amber-400 text-black'
                  : 'text-white/40 hover:text-white/70 hover:bg-white/5'
              }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {opt.badge}
            </button>
          ))}
        </div>

        {/* Active card */}
        <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">
          {/* Left: headline + features */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <i className={`${active.icon} text-2xl text-amber-400/70`}></i>
              <span
                className="text-white/40 text-xs font-medium tracking-widest uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {active.badge}
              </span>
            </div>
            <h3
              className="text-2xl md:text-4xl font-normal text-white mb-4 leading-snug"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {active.headline}
            </h3>
            <p
              className="text-white/40 text-base font-light mb-10 leading-relaxed"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {active.title}
            </p>

            <ul className="space-y-4">
              {active.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-4">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-amber-400/70 text-lg"></i>
                  </div>
                  <span
                    className="text-white/60 text-sm md:text-base font-light leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => {
                const ids = ['third-party', 'own-doctor', 'our-expert'];
                if (typeof window.REACT_APP_NAVIGATE === 'function') {
                  window.REACT_APP_NAVIGATE(`/book-treatment?supervision=${ids[activeIndex]}`);
                }
              }}
              className="mt-10 flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-8 py-4 font-semibold text-sm tracking-widest uppercase transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {t('supervision.chooseOption')}
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          {/* Right: editorial image panel */}
          <div className="relative">
            <div className="relative overflow-hidden" style={{ height: '420px' }}>
              <img
                src="https://public.readdy.ai/ai/img_res/d1467877e930898f1c600b40e676fabb.jpg"
                alt="Doctor supervision"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              {/* Stat overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p
                  className="text-white/50 text-xs font-medium tracking-widest uppercase mb-2"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {t('supervision.badge')}
                </p>
                <p
                  className="text-white text-lg font-normal leading-relaxed"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {t('supervision.why.desc')}
                </p>
              </div>
            </div>
            {/* Gold border accent */}
            <div className="absolute -bottom-3 -right-3 w-24 h-24 border border-amber-400/20" />
          </div>
        </div>
      </div>
    </section>
  );
}
